import { useEffect, useState } from "react";
import { activateUser, createUser, updateUser } from "../../services/userService";
import { requestPasswordReset } from "../../services/authService";
import PasswordField from "../ui/PasswordField";
import Button from "../ui/Button";
import Select from "../ui/Select";
import { toast } from "react-toastify";
import styles from "./UserForm.module.css";
import { buildUserSchema } from "../../schemas/userSchema";
import { passwordResetRequestSchema, zodErrorToFirstMessage } from "../../schemas/formSchemas";
import { getSystemRoles } from "../../services/appSettingsService";

const fieldClassName = styles.field;

const DashboardUserForm = ({
  user,
  readOnly = false,
  onSuccess,
  formTitle = "Dashboard User Details",
  societies = [],
  defaultSocietyId = "",
  defaultSystemRole = "Admin",
}) => {
  const [form, setForm] = useState({
    full_name: "",
    email_id: "",
    mobile_number: "",
    gender: "Male",
    system_role: defaultSystemRole,
    society_id: defaultSocietyId ? String(defaultSocietyId) : "",
    password_hash: "",
    department: "",
    access_level: "Full",
  });

  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [deactivatedUser, setDeactivatedUser] = useState(null);
  const [activating, setActivating] = useState(false);

  const availableSystemRoles = Array.from(new Set([...getSystemRoles()]));

  useEffect(() => {
    if (user) {
      setForm({
        full_name: user.full_name || "",
        email_id: user.email_id || "",
        mobile_number: user.mobile_number || "",
        gender: user.gender || "Male",
        system_role: user.system_role || user.role || defaultSystemRole,
        society_id: user.society_id ? String(user.society_id) : (defaultSocietyId ? String(defaultSocietyId) : ""),
        password_hash: "",
        department: user.role_details?.department || "",
        access_level: user.role_details?.access_level || "Full",
      });
      setError("");
      setDeactivatedUser(null);
      return;
    }

    setForm({
      full_name: "",
      email_id: "",
      mobile_number: "",
      gender: "Male",
      system_role: defaultSystemRole,
      society_id: defaultSocietyId ? String(defaultSocietyId) : "",
      password_hash: "",
      department: "",
      access_level: "Full",
    });
    setError("");
    setDeactivatedUser(null);
  }, [defaultSocietyId, defaultSystemRole, user]);

  const handleChange = (e) => {
    if (readOnly) return;
    setError("");
    if (deactivatedUser) setDeactivatedUser(null);

    const { name, value } = e.target;
    if (name === "mobile_number") {
      const sanitized = value.replace(/\D/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, [name]: sanitized }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleResendLink = async () => {
    const parsed = passwordResetRequestSchema.safeParse({
      email: form.email_id,
      society_id: form.society_id || undefined,
    });
    if (!parsed.success) {
      setError(zodErrorToFirstMessage(parsed.error));
      return;
    }

    setResendLoading(true);
    try {
      await requestPasswordReset(parsed.data);
      toast.success("Password setup link sent to user email.");
    } catch (err) {
      console.error(err);
      const apiMessage = err?.data?.error || err?.data?.message || err?.message;
      toast.error(apiMessage || "Unable to resend setup link.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleActivateDeactivatedUser = async () => {
    if (!deactivatedUser?.userId) return;
    setActivating(true);
    try {
      await activateUser(deactivatedUser.userId, {
        full_name: form.full_name || undefined,
        primary_role: "System",
        system_role: form.system_role,
        secondary_role: "",
        society_id: form.society_id ? Number(form.society_id) : null,
        password_hash: form.password_hash || undefined,
        role_details: {
          department: form.department,
          access_level: form.access_level,
        },
      });

      toast.success("Dashboard user activated successfully!");
      setDeactivatedUser(null);
      if (onSuccess) onSuccess();
    } catch (actErr) {
      console.error(actErr);
      const msg = actErr?.data?.error || actErr?.data?.message || actErr?.message || "Failed to activate user.";
      toast.error(msg);
    } finally {
      setActivating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setError("");

    const schema = buildUserSchema({
      showPasswordField: true,
      passwordRequired: !user,
    });

    const parsed = schema.safeParse({
      full_name: form.full_name,
      email_id: form.email_id,
      mobile_number: form.mobile_number,
      gender: form.gender,
      primary_role: "System",
      system_role: form.system_role,
      secondary_role: "",
      has_portal_access: true,
      society_id: form.society_id || undefined,
      password_hash: form.password_hash,
      role_details: {
        department: form.department,
        access_level: form.access_level,
      },
    });

    if (!parsed.success) {
      setError(zodErrorToFirstMessage(parsed.error));
      return;
    }

    try {
      const payload = {
        ...parsed.data,
        account_type: "management",
        has_portal_access: true,
        primary_role: "System",
        system_role: form.system_role,
        secondary_role: "",
        society_id: form.society_id ? Number(form.society_id) : null,
        role_details: {
          department: form.department,
          access_level: form.access_level,
        },
      };

      setDeactivatedUser(null);
      if (user) {
        await updateUser(user.user_id, payload);
        toast.success("Dashboard user updated successfully.");
      } else {
        await createUser(payload);
        toast.success("Dashboard user created successfully.");
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      const apiData = err?.data;
      const apiMessage = apiData?.error || apiData?.message || err?.message;

      if (apiData?.is_deactivated && apiData?.user_id) {
        setDeactivatedUser({
          userId: apiData.user_id,
          name: apiData.full_name,
          email: apiData.email_id || form.email_id,
          field: apiData.field || "email_id",
          message: apiData.error || "Email exists for deactivated user.",
        });
        setError("");
      } else {
        setDeactivatedUser(null);
        setError(apiMessage || "Unable to save dashboard user right now.");
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {formTitle && (
        <div className={styles.headerRow}>
          <div>
            <h3 className={styles.headerTitle}>{formTitle}</h3>
            <p className={styles.headerHint}>
              Assign platform-wide or property-scoped system roles for portal administration.
            </p>
          </div>

          {user?.user_id ? (
            <div className={styles.headerActions}>
              <Button
                type="button"
                variant="outline"
                loading={resendLoading}
                onClick={handleResendLink}
              >
                Resend Link
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {error && <p className={styles.error}>{error}</p>}

      {deactivatedUser && (
        <div className={styles.deactivatedBanner}>
          <div className={styles.deactivatedText}>
            <span className={styles.deactivatedTitle}>{deactivatedUser.message}</span>
            <span className={styles.deactivatedDesc}>
              User <strong>{deactivatedUser.name || deactivatedUser.email}</strong> is currently deactivated.
            </span>
          </div>
          <button
            type="button"
            className={styles.activateBtn}
            disabled={activating}
            onClick={handleActivateDeactivatedUser}
          >
            {activating ? "Activating..." : "Activate User?"}
          </button>
        </div>
      )}

      <div className={styles.grid}>
        <div className={`${styles.label} ${styles.fullSpan}`}>
          <span className={styles.labelText} style={{ marginBottom: "6px", display: "block" }}>
            System Role <span style={{ color: "#ef4444" }}>*</span>
          </span>
          <Select
            value={form.system_role}
            onChange={(e) => handleChange({ target: { name: 'system_role', value: e.target.value } })}
            disabled={readOnly}
            options={availableSystemRoles.map((role) => ({
              label: role,
              value: role,
            }))}
          />
          <span className={styles.helperText} style={{ marginTop: "4px", display: "block" }}>
            Dashboard access is restricted strictly to verified System Roles.
          </span>
        </div>

        <label className={styles.label}>
          <span className={styles.labelText}>
            Full Name <span style={{ color: "#ef4444" }}>*</span>
          </span>
          <input
            name="full_name"
            value={form.full_name}
            onChange={handleChange}
            disabled={readOnly}
            placeholder="Full Name"
            required
            className={fieldClassName}
          />
        </label>

        <label className={styles.label}>
          <span className={styles.labelText}>
            Email <span style={{ color: "#ef4444" }}>*</span>
          </span>
          <input
            type="email"
            name="email_id"
            value={form.email_id}
            onChange={handleChange}
            disabled={readOnly}
            placeholder="name@asmitagroup.com"
            required
            className={fieldClassName}
          />
        </label>

        <label className={styles.label}>
          <span className={styles.labelText}>
            Mobile Number <span style={{ color: "#ef4444" }}>*</span>
          </span>
          <input
            type="tel"
            name="mobile_number"
            value={form.mobile_number}
            onChange={handleChange}
            disabled={readOnly}
            placeholder="10-digit mobile number"
            inputMode="numeric"
            maxLength={10}
            required
            className={fieldClassName}
          />
        </label>

        <div>
          <span className={styles.labelText} style={{ marginBottom: "6px", display: "block" }}>Gender</span>
          <Select
            value={form.gender}
            onChange={(e) => handleChange({ target: { name: 'gender', value: e.target.value } })}
            disabled={readOnly}
            options={[
              { label: "Male", value: "Male" },
              { label: "Female", value: "Female" }
            ]}
          />
        </div>

        <div className={`${styles.label} ${styles.fullSpan}`}>
          <span className={styles.labelText} style={{ marginBottom: "6px", display: "block" }}>
            Property / Society <span style={{ color: "#64748b", fontSize: "11px", fontWeight: "normal" }}>(Optional — Leave blank for Platform-wide access)</span>
          </span>
          <Select
            value={form.society_id || ""}
            onChange={(e) => handleChange({ target: { name: 'society_id', value: e.target.value } })}
            disabled={readOnly}
            options={[
              { label: "Platform-wide (All Properties)", value: "" },
              ...societies.map((s) => ({
                label: `${s.society_name} ${s.society_type ? `(${s.society_type})` : ""}`,
                value: String(s.society_id),
              }))
            ]}
          />
        </div>

        {!readOnly && (
          <div className={styles.fullSpan}>
            <PasswordField
              label="Portal Password"
              name="password_hash"
              value={form.password_hash}
              onChange={handleChange}
              placeholder={user ? "Leave blank to keep current password" : "Enter portal password"}
              required={!user}
            />
          </div>
        )}

        <label className={styles.label}>
          <span className={styles.labelText}>Department</span>
          <input
            name="department"
            value={form.department}
            onChange={handleChange}
            disabled={readOnly}
            placeholder="e.g. IT, Finance, Operations"
            className={fieldClassName}
          />
        </label>

        <div>
          <span className={styles.labelText} style={{ marginBottom: "6px", display: "block" }}>Access Level</span>
          <Select
            value={form.access_level}
            onChange={(e) => handleChange({ target: { name: 'access_level', value: e.target.value } })}
            disabled={readOnly}
            options={[
              { label: "Full Access", value: "Full" },
              { label: "Read-Only", value: "Read-Only" },
              { label: "Restricted", value: "Restricted" }
            ]}
          />
        </div>
      </div>

      {!readOnly && (
        <button type="submit" className={styles.submit}>
          {user ? "Update Dashboard User" : "Create Dashboard User"}
        </button>
      )}
    </form>
  );
};

export default DashboardUserForm;
